import React, { useState, useEffect, useCallback, useRef } from "react";
import { Tree, Spin, Card, Typography } from "antd";
import { BranchesOutlined } from "@ant-design/icons";
import { connect } from "react-redux";
import { getGroupMembersForTreeView } from "../../actions/api";
import TooltipIcon from "../common/TooltipIcon";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { showErrorNotification } from "../../util";

const GroupTreeView = ({ getGroupMembersForTreeView }) => {
  const [treeData, setTreeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const fields = ["groupIdentifier", "memberGroupIds"];
  const selectedGroup = useSelector((state) => state.groups.selectedGroup);
  const { id } = useParams();
  const navigate = useNavigate();

  const visitedNodes = useRef(new Set());
  const loadedChildren = useRef(new Map());
  const keyCounter = useRef(0);

  useEffect(() => {
    const fetchGroupData = async () => {
      setLoading(true);
      visitedNodes.current.clear();
      loadedChildren.current.clear();
      keyCounter.current = 0;

      try {
        const groupResponse = await getGroupMembersForTreeView(id, fields);
        const parentResponses = groupResponse.payload.data;

        const wrappedResponse = [
          {
            groupIdentifier: selectedGroup?.groupIdentifier,
            id: id,
            memberGroupIds: [...parentResponses],
          },
        ];

        if (parentResponses.length > 0) {
          setTreeData(transformToTreeObject(wrappedResponse));
        } else {
          setTreeData([]);
        }
      } catch (error) {
        showErrorNotification(error.payload.title, error.payload.data.message);
      } finally {
        setLoading(false);
      }
    };

    fetchGroupData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Generate a unique key for each node while preserving the original ID
  const generateUniqueKey = (id) => {
    return `${id}-${keyCounter.current++}`;
  };

  const transformToTreeObject = (obj, parentPath = []) => {
    return obj.map((item) => {
      // Check for circular dependency
      const isCircular = parentPath.includes(item.id);

      if (isCircular) {
        // Mark circular nodes as leaves with a special key
        return {
          title: item.groupIdentifier,
          key: generateUniqueKey(`${item.id}-circular`),
          isLeaf: true,
          icon: <BranchesOutlined />,
          // Store the original ID for navigation
          originalId: item.id,
        };
      }

      const hasCachedChildren = loadedChildren.current.has(item.id);

      return {
        title: item.groupIdentifier,
        key: generateUniqueKey(item.id),
        isLeaf:
          !(item.memberGroupIds && item.memberGroupIds.length > 0) &&
          !hasCachedChildren,
        icon: <BranchesOutlined />,
        // Store the original ID for navigation
        originalId: item.id,
      };
    });
  };

  const insertChildrenIntoTree = (nodes, targetKey, children) => {
    return nodes.map((node) => {
      if (node.key === targetKey) {
        return {
          ...node,
          isLeaf: children.length === 0,
          children: children,
        };
      }

      if (node.children?.length) {
        return {
          ...node,
          children: insertChildrenIntoTree(node.children, targetKey, children),
        };
      }

      return node;
    });
  };

  const onLoadData = useCallback(
    async (node) => {
      // Use the original ID for API calls, not the unique key
      const nodeId = node.originalId;

      // Check for circular dependency
      if (visitedNodes.current.has(nodeId)) {
        return;
      }
      visitedNodes.current.add(nodeId);

      // Check if we've already loaded children for this node
      if (loadedChildren.current.has(nodeId)) {
        const children = loadedChildren.current.get(nodeId);
        setTreeData((prev) => insertChildrenIntoTree(prev, node.key, children));
        return;
      }

      try {
        const res = await getGroupMembersForTreeView(nodeId, fields);

        if (res.payload.data) {
          // Get parent original IDs to check for circular dependencies
          const parentIds = getParentIds(treeData, node.key);

          // Convert children to tree format
          const children = transformToTreeObject(res.payload.data, [
            ...parentIds,
            nodeId,
          ]);

          // Cache the children
          loadedChildren.current.set(nodeId, children);

          // Inject into tree
          setTreeData((prev) =>
            insertChildrenIntoTree(prev, node.key, children)
          );
        }
      } catch (error) {
        console.error("Error loading node data:", error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getGroupMembersForTreeView, treeData]
  );

  // Helper function to get all parent original IDs for a node
  const getParentIds = (nodes, targetKey, parentIds = []) => {
    for (const node of nodes) {
      if (node.key === targetKey) {
        return parentIds;
      }

      if (node.children) {
        const found = getParentIds(node.children, targetKey, [
          ...parentIds,
          node.originalId,
        ]);
        if (found) return found;
      }
    }

    return [];
  };

  const navigateToGroup = useCallback(
    (selectedKeys, { node }) => {
      if (
        selectedKeys.length > 0 &&
        node.originalId &&
        !node.key.includes("circular")
      ) {
        navigate(`/group/${node.originalId}/details`);
      }
    },
    [navigate]
  );

  return (
    <Card
      title={
        <Typography.Title level={4} style={{ margin: 0 }}>
          <BranchesOutlined style={{ marginRight: 8, color: "#bfbfbf" }} />
          Group tree data
          <TooltipIcon
            size={16}
            title="Groups that contain other groups can be expanded by clicking. Circular dependencies are automatically handled."
          />
        </Typography.Title>
      }
    >
      <Spin spinning={loading}>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Browse nested group members of any groups included directly in your
          group. Identity members are not shown in order to respect user
          privacy.
        </Typography.Paragraph>

        {treeData.length > 0 ? (
          <Tree
            onSelect={navigateToGroup}
            showIcon
            multiple={false}
            treeData={treeData}
            loadData={onLoadData}
          />
        ) : (
          <Typography.Text type="secondary">
            No group members found.
          </Typography.Text>
        )}
      </Spin>
    </Card>
  );
};

export default connect(null, {
  getGroupMembersForTreeView,
})(GroupTreeView);
